import { deepCopy } from "@/utils/utils";
import { uuid } from "@/utils/componentId";
import { BACK_COMPONENT } from "@/utils/attrNameData";
import {
  listBackCandidates,
  defaultKeepBackIndex,
  resolveBackChoice,
} from "@/utils/canvasBackDuplicate";

function compDisplayLabel(comp) {
  if (!comp) {
    return "?";
  }
  if (comp.component === "v-button") {
    return (
      comp.propValue ||
      (comp.style && comp.style.alias) ||
      comp.label ||
      comp.id
    );
  }
  return comp.label || comp.component || comp.id;
}

function findBackIndex(pageArr) {
  if (!Array.isArray(pageArr)) {
    return -1;
  }
  return pageArr.findIndex(
    (c) => c && (c.component === "Back" || c.id === "Back")
  );
}

function findDuplicateIdGroups(pageArr) {
  const byId = new Map();
  pageArr.forEach((comp, index) => {
    if (!comp || comp.id == null || comp.id === "" || comp.id === "Back") {
      return;
    }
    const id = String(comp.id);
    if (!byId.has(id)) {
      byId.set(id, []);
    }
    byId.get(id).push({
      index,
      label: compDisplayLabel(comp),
      component: comp.component,
    });
  });
  const groups = [];
  byId.forEach((items, id) => {
    if (items.length > 1) {
      groups.push({ id, items });
    }
  });
  return groups;
}

function inferPageNameFromArr(pageArr, fallback = "新页面") {
  const backIdx = findBackIndex(pageArr);
  if (backIdx >= 0) {
    const n = pageArr[backIdx].style && pageArr[backIdx].style.pageName;
    if (n) {
      return n;
    }
  }
  for (let i = 0; i < pageArr.length; i++) {
    const n = pageArr[i] && pageArr[i].style && pageArr[i].style.pageName;
    if (n) {
      return n;
    }
  }
  return fallback;
}

/**
 * 扫描单页画布 JSON 中与编辑器/runtime 不一致的常见问题。
 * @returns {Array<{ key: string, type: string, title: string, hint: string, candidates?: Array, defaultChoice: * }>}
 */
export function scanPageAnomalies(pageArr) {
  if (!Array.isArray(pageArr) || pageArr.length === 0) {
    return [];
  }
  const list = [];
  const backs = listBackCandidates(pageArr);
  if (backs.length > 1) {
    list.push({
      key: "duplicate_back",
      type: "duplicate_back",
      title: "多个页面背景",
      hint:
        "该页存在多个「背景」组件，画布网格只认列表第一项的背景图，下载清单也会扫到隐藏层。请保留一个。",
      candidates: backs,
      defaultChoice: defaultKeepBackIndex(pageArr),
    });
  }
  const backIdx = findBackIndex(pageArr);
  if (backIdx === -1) {
    list.push({
      key: "missing_back",
      type: "missing_back",
      title: "缺少页面背景",
      hint:
        "该页没有「背景」组件，画布尺寸与页面名可能异常。将自动在列表首位补全默认背景。",
      defaultChoice: true,
    });
  } else if (backs.length <= 1 && backIdx !== 0) {
    list.push({
      key: "back_not_first",
      type: "back_not_first",
      title: "背景不在组件列表首位",
      hint:
        "画布背景图、网格、页面底色取自 componentData[0]，当前背景不在第一位，可能导致显示与保存不一致。建议移到首位。",
      defaultChoice: true,
      backIndex: backIdx,
    });
  }
  findDuplicateIdGroups(pageArr).forEach((group) => {
    list.push({
      key: `duplicate_id:${group.id}`,
      type: "duplicate_id",
      title: `组件 ID 重复（${group.id}）`,
      hint:
        "同一页两个组件 id 相同时，选中、联动、复制容易指向错误组件。请指定保留 id 的组件，其余将分配新 id。",
      id: group.id,
      candidates: group.items,
      defaultChoice: group.items[0] ? group.items[0].index : 0,
    });
  });
  return list;
}

export function hasPageAnomalies(pageArr) {
  return scanPageAnomalies(pageArr).length > 0;
}

/**
 * 扫描工程内待保存页面的数据异常。
 * @param {Object} pageDatas pageId -> componentData[]
 * @param {(pageKey: string) => boolean} shouldIncludePage
 * @param {(pageKey: string) => string} getPageName
 */
export function collectProjectPageIntegrityIssues(
  pageDatas,
  shouldIncludePage,
  getPageName
) {
  const issues = [];
  if (!pageDatas || typeof pageDatas !== "object") {
    return issues;
  }
  Object.keys(pageDatas).forEach((pageKey) => {
    if (shouldIncludePage && !shouldIncludePage(pageKey)) {
      return;
    }
    const pageArr = pageDatas[pageKey];
    if (!Array.isArray(pageArr) || pageArr.length === 0) {
      return;
    }
    const anomalies = scanPageAnomalies(pageArr);
    if (!anomalies.length) {
      return;
    }
    issues.push({
      pageKey: String(pageKey),
      pageName:
        (typeof getPageName === "function" && getPageName(pageKey)) ||
        String(pageKey),
      pageArr,
      anomalies,
    });
  });
  return issues;
}

export function summarizePageIntegrityIssues(issues) {
  if (!issues || !issues.length) {
    return "";
  }
  const names = issues
    .map((i) => i.pageName || i.pageKey)
    .filter(Boolean)
    .slice(0, 5);
  const more =
    issues.length > names.length ? ` 等共 ${issues.length} 页` : "";
  return `页面 ${names.join("、")}${more} 存在背景重复、缺少背景、背景顺序或组件 ID 重复等问题，请确认后再保存。`;
}

export function buildDefaultAnomalyChoices(anomalies) {
  const choices = {};
  (anomalies || []).forEach((a) => {
    choices[a.key] = a.defaultChoice;
  });
  return choices;
}

function findKeepIndexForDuplicateId(pageArr, duplicateId, choice, candidates) {
  const idStr = String(duplicateId);
  const indices = [];
  pageArr.forEach((comp, i) => {
    if (comp && String(comp.id) === idStr) {
      indices.push(i);
    }
  });
  if (indices.length <= 1) {
    return indices[0];
  }
  let candidate = null;
  if (candidates && choice != null) {
    candidate = candidates.find((c) => c.index === choice) || null;
  }
  if (candidate) {
    for (let k = 0; k < indices.length; k++) {
      const i = indices[k];
      const c = pageArr[i];
      if (
        compDisplayLabel(c) === candidate.label &&
        c.component === candidate.component
      ) {
        return i;
      }
    }
  }
  if (typeof choice === "number" && indices.indexOf(choice) !== -1) {
    return choice;
  }
  return indices[0];
}

function resolveDuplicateIdGroup(pageArr, duplicateId, choice, candidates) {
  const ret = deepCopy(pageArr);
  const keepIdx = findKeepIndexForDuplicateId(
    ret,
    duplicateId,
    choice,
    candidates
  );
  const idStr = String(duplicateId);
  ret.forEach((comp, i) => {
    if (!comp || String(comp.id) !== idStr || i === keepIdx) {
      return;
    }
    comp.id = uuid();
  });
  return ret;
}

function moveBackToFirst(pageArr) {
  const ret = deepCopy(pageArr);
  const bi = findBackIndex(ret);
  if (bi <= 0) {
    return ret;
  }
  const back = ret.splice(bi, 1)[0];
  ret.unshift(back);
  return ret;
}

function prependMissingBack(pageArr, pageName) {
  const name = pageName || inferPageNameFromArr(pageArr);
  return [BACK_COMPONENT(name), ...deepCopy(pageArr)];
}

/**
 * 按用户在对话框中的选择依次修复（顺序与 scan 一致）。
 */
export function applyPageIntegrityChoices(pageArr, anomalies, choices) {
  let arr = deepCopy(pageArr);
  (anomalies || []).forEach((a) => {
    const choice = choices && Object.prototype.hasOwnProperty.call(choices, a.key)
      ? choices[a.key]
      : a.defaultChoice;
    if (a.type === "duplicate_back") {
      if (choice != null && choice !== false) {
        arr = resolveBackChoice(arr, choice);
      }
    } else if (a.type === "missing_back") {
      if (choice !== false && findBackIndex(arr) === -1) {
        arr = prependMissingBack(arr);
      }
    } else if (a.type === "back_not_first") {
      if (choice !== false && findBackIndex(arr) > 0) {
        arr = moveBackToFirst(arr);
      }
    } else if (a.type === "duplicate_id") {
      if (choice != null && choice !== false) {
        arr = resolveDuplicateIdGroup(arr, a.id, choice, a.candidates);
      }
    }
  });
  return arr;
}

export { listBackCandidates, defaultKeepBackIndex, resolveBackChoice };
